import React, { useId, useMemo, useState } from 'react';
import { formatMoney } from '../../lib/format';

const ORDERS_COLOR = '#c55a1d';
const REVENUE_COLOR = '#1565c0';
const WIDTH = 720;
const HEIGHT = 300;
const PAD = { top: 36, right: 64, bottom: 52, left: 52 };
const TICKS = 5;

function niceMax(value) {
  if (value <= 0) return 5;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const f = value / exp;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * exp;
}

function pointCoords(points, key, maxVal, plotW, plotH) {
  const step = points.length > 1 ? plotW / (points.length - 1) : 0;
  const baseY = PAD.top + plotH;
  return points.map((p, i) => ({
    x: PAD.left + i * step,
    y: PAD.top + plotH - (p[key] / maxVal) * plotH,
    baseY,
    ...p,
  }));
}

function linePath(coords) {
  if (!coords.length) return '';
  return coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
}

function areaPath(coords) {
  if (!coords.length) return '';
  const baseY = coords[0].baseY;
  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
  const close = `L ${coords[coords.length - 1].x} ${baseY} L ${coords[0].x} ${baseY} Z`;
  return `${line} ${close}`;
}

function formatRevenueAxis(tick) {
  if (tick >= 100000) return `₹${(tick / 100000).toFixed(1)}L`;
  if (tick >= 1000) return `₹${(tick / 1000).toFixed(tick % 1000 === 0 ? 0 : 1)}k`;
  return `₹${tick}`;
}

export function AnalyticsDualLineChart({ points, periodLabel }) {
  const titleId = useId();
  const gradOrdersId = useId();
  const gradRevenueId = useId();
  const clipId = useId();
  const [hovered, setHovered] = useState(null);


  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const series = useMemo(() => (points?.length ? points : []), [points]);

  const chart = useMemo(() => {
    if (!series.length) return null;
    const maxOrders = niceMax(Math.max(...series.map((p) => p.orders), 1));
    const maxRevenue = niceMax(Math.max(...series.map((p) => p.revenue), 1));
    const ordersCoords = pointCoords(series, 'orders', maxOrders, plotW, plotH);
    const revenueCoords = pointCoords(series, 'revenue', maxRevenue, plotW, plotH);
    const yTicksOrders = Array.from({ length: TICKS + 1 }, (_, i) => Math.round((maxOrders / TICKS) * i));
    const yTicksRevenue = Array.from({ length: TICKS + 1 }, (_, i) => Math.round((maxRevenue / TICKS) * i));
    const step = series.length > 1 ? plotW / (series.length - 1) : 0;
    return {
      maxOrders,
      maxRevenue,
      ordersCoords,
      revenueCoords,
      ordersLine: linePath(ordersCoords),
      revenueLine: linePath(revenueCoords),
      ordersArea: areaPath(ordersCoords),
      revenueArea: areaPath(revenueCoords),
      yTicksOrders,
      yTicksRevenue,
      step,
    };
  }, [series, plotW, plotH]);

  if (!series.length || !chart) {
    return (
      <figure className="analytics-graph mb-0" aria-labelledby={titleId}>
        <div className="analytics-graph__head d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h3 className="h6 text-secondary text-uppercase letter-spacing-tight mb-1">Orders & revenue</h3>
            <p className="small text-secondary mb-0">{periodLabel}</p>
          </div>
        </div>
        <div className="analytics-graph__empty text-center py-5 px-3 rounded-3 border border-dashed bg-light">
          <p className="text-secondary mb-0">No orders in this period — chart will appear when you have sales.</p>
        </div>
      </figure>
    );
  }

  const active = hovered != null ? points[hovered] : null;
  const activeX = hovered != null ? PAD.left + hovered * chart.step : null;

  return (
    <figure className="analytics-graph mb-0" aria-labelledby={titleId}>
      <div className="analytics-graph__head d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h3 className="h6 text-secondary text-uppercase letter-spacing-tight mb-1">Orders & revenue</h3>
          <p className="small text-secondary mb-0">{periodLabel}</p>
        </div>
        <div className="analytics-line-legend d-flex flex-wrap gap-3 small">
          <span className="analytics-line-legend__item">
            <span className="analytics-line-legend__line analytics-line-legend__line--orders" aria-hidden />
            Orders
          </span>
          <span className="analytics-line-legend__item">
            <span className="analytics-line-legend__line analytics-line-legend__line--revenue" aria-hidden />
            Amount spent
          </span>
        </div>
      </div>

      <div className="analytics-graph__canvas position-relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="analytics-graph__svg w-100"
          role="img"
          aria-labelledby={titleId}
          onMouseLeave={() => setHovered(null)}
        >
          <title id={titleId}>Orders and revenue timeline for {periodLabel}</title>
          <defs>
            <linearGradient id={gradOrdersId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ORDERS_COLOR} stopOpacity="0.28" />
              <stop offset="100%" stopColor={ORDERS_COLOR} stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id={gradRevenueId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={REVENUE_COLOR} stopOpacity="0.22" />
              <stop offset="100%" stopColor={REVENUE_COLOR} stopOpacity="0.02" />
            </linearGradient>
            <clipPath id={clipId}>
              <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} rx="6" />
            </clipPath>
          </defs>

          <rect
            x={PAD.left}
            y={PAD.top}
            width={plotW}
            height={plotH}
            rx="8"
            className="analytics-graph__plot-bg"
          />

          {chart.yTicksOrders.map((tick) => {
            const y = PAD.top + plotH - (tick / chart.maxOrders) * plotH;
            return (
              <g key={`h-${tick}`}>
                <line x1={PAD.left} y1={y} x2={PAD.left + plotW} y2={y} className="analytics-graph__grid-h" />
                <text x={PAD.left - 10} y={y + 4} textAnchor="end" className="analytics-graph__ylabel analytics-graph__ylabel--orders">
                  {tick}
                </text>
              </g>
            );
          })}

          {chart.yTicksRevenue.map((tick) => {
            const y = PAD.top + plotH - (tick / chart.maxRevenue) * plotH;
            return (
              <text
                key={`hr-${tick}`}
                x={PAD.left + plotW + 10}
                y={y + 4}
                textAnchor="start"
                className="analytics-graph__ylabel analytics-graph__ylabel--revenue"
              >
                {formatRevenueAxis(tick)}
              </text>
            );
          })}

          {points.map((p, i) => {
            const x = PAD.left + i * chart.step;
            return (
              <line
                key={`v-${p.label}`}
                x1={x}
                y1={PAD.top}
                x2={x}
                y2={PAD.top + plotH}
                className="analytics-graph__grid-v"
              />
            );
          })}

          <g clipPath={`url(#${clipId})`}>
            <path d={chart.revenueArea} fill={`url(#${gradRevenueId})`} />
            <path d={chart.ordersArea} fill={`url(#${gradOrdersId})`} />
            <path d={chart.revenueLine} className="analytics-graph__line analytics-graph__line--revenue" fill="none" />
            <path d={chart.ordersLine} className="analytics-graph__line analytics-graph__line--orders" fill="none" />
          </g>

          {activeX != null ? (
            <line
              x1={activeX}
              y1={PAD.top}
              x2={activeX}
              y2={PAD.top + plotH}
              className="analytics-graph__crosshair"
            />
          ) : null}

          {chart.ordersCoords.map((c, i) => (
            <g key={`hit-${c.label}`}>
              <rect
                x={c.x - chart.step / 2}
                y={PAD.top}
                width={chart.step}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHovered(i)}
              />
              <circle
                cx={c.x}
                cy={c.y}
                r={hovered === i ? 6 : 4}
                className="analytics-graph__dot analytics-graph__dot--orders"
              />
              <circle
                cx={chart.revenueCoords[i].x}
                cy={chart.revenueCoords[i].y}
                r={hovered === i ? 6 : 4}
                className="analytics-graph__dot analytics-graph__dot--revenue"
              />
            </g>
          ))}

          <line x1={PAD.left} y1={PAD.top + plotH} x2={PAD.left + plotW} y2={PAD.top + plotH} className="analytics-graph__axis" />
          <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + plotH} className="analytics-graph__axis" />
          <line
            x1={PAD.left + plotW}
            y1={PAD.top}
            x2={PAD.left + plotW}
            y2={PAD.top + plotH}
            className="analytics-graph__axis analytics-graph__axis--right"
          />

          {points.map((p, i) => (
            <text
              key={`x-${p.label}`}
              x={PAD.left + i * chart.step}
              y={HEIGHT - 18}
              textAnchor="middle"
              className="analytics-graph__xlabel"
            >
              {p.label}
            </text>
          ))}

          <text x={14} y={PAD.top + plotH / 2} textAnchor="middle" transform={`rotate(-90, 14, ${PAD.top + plotH / 2})`} className="analytics-graph__axis-title analytics-graph__axis-title--orders">
            Orders
          </text>
          <text
            x={WIDTH - 14}
            y={PAD.top + plotH / 2}
            textAnchor="middle"
            transform={`rotate(90, ${WIDTH - 14}, ${PAD.top + plotH / 2})`}
            className="analytics-graph__axis-title analytics-graph__axis-title--revenue"
          >
            Amount (₹)
          </text>
          <text x={PAD.left + plotW / 2} y={HEIGHT - 4} textAnchor="middle" className="analytics-graph__axis-title analytics-graph__axis-title--x">
            Timeline
          </text>
        </svg>

        {active && activeX != null ? (
          <div
            className="analytics-graph__tooltip"
            style={{
              left: `${(activeX / WIDTH) * 100}%`,
            }}
          >
            <div className="analytics-graph__tooltip-label fw-semibold">{active.label}</div>
            <div className="analytics-graph__tooltip-row">
              <span className="analytics-graph__tooltip-dot analytics-graph__tooltip-dot--orders" />
              <span>Orders</span>
              <strong className="ms-auto tabular-nums">{active.orders}</strong>
            </div>
            <div className="analytics-graph__tooltip-row">
              <span className="analytics-graph__tooltip-dot analytics-graph__tooltip-dot--revenue" />
              <span>Spent</span>
              <strong className="ms-auto tabular-nums">{formatMoney(active.revenue)}</strong>
            </div>
          </div>
        ) : null}
      </div>

      <figcaption className="visually-hidden">
        Line chart for {periodLabel} showing order count and customer spend over time.
      </figcaption>
    </figure>
  );
}

export function AnalyticsChartSummary({ points }) {
  if (!points?.length) return null;

  const totalOrders = points.reduce((n, p) => n + p.orders, 0);
  const totalRevenue = points.reduce((n, p) => n + p.revenue, 0);
  const peakOrders = Math.max(...points.map((p) => p.orders), 0);
  const peakRevenue = Math.max(...points.map((p) => p.revenue), 0);

  return (
    <div className="row g-3 mt-3 pt-3 border-top analytics-chart-summary">
      <div className="col-6 col-md-3">
        <div className="small text-secondary">Total orders</div>
        <div className="fw-semibold text-dark tabular-nums">{totalOrders}</div>
      </div>
      <div className="col-6 col-md-3">
        <div className="small text-secondary">Total spent</div>
        <div className="fw-semibold tabular-nums" style={{ color: REVENUE_COLOR }}>
          {formatMoney(totalRevenue)}
        </div>
      </div>
      <div className="col-6 col-md-3">
        <div className="small text-secondary">Peak orders</div>
        <div className="fw-semibold tabular-nums" style={{ color: ORDERS_COLOR }}>
          {peakOrders}
        </div>
      </div>
      <div className="col-6 col-md-3">
        <div className="small text-secondary">Peak spend</div>
        <div className="fw-semibold tabular-nums" style={{ color: REVENUE_COLOR }}>
          {formatMoney(peakRevenue)}
        </div>
      </div>
    </div>
  );
}
